/**
 * Cache Utilities for Frontend API Caching
 * Reduces unnecessary API calls by implementing intelligent caching
 */

// Cache expiration time in milliseconds (5 minutes by default)
const DEFAULT_CACHE_EXPIRY = 5 * 60 * 1000;

/**
 * Generate a unique cache key based on API parameters
 * @param {string} endpoint - The API endpoint identifier
 * @param {object} params - Parameters for the API call
 * @returns {string} - Unique cache key
 */
export const generateCacheKey = (endpoint, params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((result, key) => {
      result[key] = params[key];
      return result;
    }, {});
  
  return `${endpoint}_${JSON.stringify(sortedParams)}`;
};

/**
 * Check if cached data is still valid
 * @param {object} cacheEntry - Cache entry with data and timestamp
 * @param {number} expiryTime - Custom expiry time in milliseconds
 * @returns {boolean} - True if cache is valid
 */
export const isCacheValid = (cacheEntry, expiryTime = DEFAULT_CACHE_EXPIRY) => {
   // TEMPORARILY DISABLED
  return false;
  if (!cacheEntry || !cacheEntry.timestamp) {
    return false;
  }
  
  const now = Date.now();
  return (now - cacheEntry.timestamp) < expiryTime;
};

/**
 * Create cache entry with timestamp
 * @param {any} data - Data to cache
 * @returns {object} - Cache entry with data and timestamp
 */
export const createCacheEntry = (data) => ({
  data,
  timestamp: Date.now()
});

/**
 * Get cache key for health parameters
 * @param {string} paramType - Type of health parameter (bloodSugar, bloodPressure, etc.)
 * @param {object} options - Request options
 * @returns {string} - Cache key
 */
export const getHealthParamCacheKey = (paramType, options) => {
  const { token, duration, date, appointmentId, patientId } = options;
  
  // Create cache key without including token for security
  const cacheParams = {
    paramType,
    duration,
    date,
    appointmentId: appointmentId || '',
    patientId: patientId || '',
    userType: localStorage.getItem("userType") || 'patient'
  };
  
  return generateCacheKey('healthParams', cacheParams);
};

/**
 * Cache configurations for different API endpoints
 */
export const CACHE_CONFIGS = {
  HEALTH_PARAMS: {
    expiry: 5 * 60 * 1000, // 5 minutes
    maxSize: 100 // Maximum number of cache entries
  },
  APPOINTMENTS: {
    expiry: 2 * 60 * 1000, // 2 minutes
    maxSize: 50
  },
  LIPID_PROFILE: {
    expiry: 10 * 60 * 1000, // 10 minutes
    maxSize: 30
  },
  MEDICAL_HISTORY: {
    expiry: 15 * 60 * 1000, // 15 minutes
    maxSize: 20
  },
  PROFILES: {
    expiry: 20 * 60 * 1000, // 20 minutes (profiles don't change often)
    maxSize: 10 // Small cache size for profiles
  }
};

/**
 * Clean up old cache entries based on LRU (Least Recently Used) strategy
 * @param {object} cache - Current cache object
 * @param {number} maxSize - Maximum number of entries to keep
 * @returns {object} - Cleaned cache
 */
export const cleanupCache = (cache, maxSize) => {
  const entries = Object.entries(cache);
  
  if (entries.length <= maxSize) {
    return cache;
  }
  
  // Sort by timestamp (most recent first)
  const sortedEntries = entries.sort((a, b) => 
    (b[1].timestamp || 0) - (a[1].timestamp || 0)
  );
  
  // Keep only the most recent entries
  const cleanedEntries = sortedEntries.slice(0, maxSize);
  
  return cleanedEntries.reduce((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});
};

/**
 * Invalidate cache entries by pattern
 * @param {object} cache - Current cache object
 * @param {string} pattern - Pattern to match for invalidation
 * @returns {object} - Cache with matching entries removed
 */
export const invalidateCacheByPattern = (cache, pattern) => {
  const newCache = {};
  
  Object.keys(cache).forEach(key => {
    if (!key.includes(pattern)) {
      newCache[key] = cache[key];
    }
  });
  
  return newCache;
};

/**
 * Get all cache keys for a specific user/patient
 * @param {string} userId - User or patient ID
 * @returns {string} - Pattern to match user-specific cache entries
 */
export const getUserCachePattern = (userId) => {
  return `patientId":"${userId}`;
};

/**
 * Batch invalidation patterns for different scenarios
 */
export const CACHE_INVALIDATION_PATTERNS = {
  // Invalidate all health params for a specific date
  HEALTH_PARAMS_BY_DATE: (date) => `"date":"${date}"`,
  
  // Invalidate all health params for a specific patient
  HEALTH_PARAMS_BY_PATIENT: (patientId) => `"patientId":"${patientId}"`,
  
  // Invalidate all appointment-related cache
  APPOINTMENTS_BY_USER: (userId) => `appointments_${userId}`,
  
  // Invalidate all cache for a user when they log out
  ALL_USER_DATA: (userId) => userId
};

/**
 * Debounce function to prevent rapid API calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
